import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alpha, useTheme } from '@mui/material/styles';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import {
  DemoPersonaDTO,
  AccountType,
  getDemoPersonas,
  deleteDemoPersona,
  seedDefaultPersonas,
  createDemoPersona,
  updateDemoPersona,
  CreateDemoPersonaRequest,
} from './DemoEngineService';

const fmtCurrency = (amount: number | null, freq: string) =>
  amount != null
    ? `£${amount.toLocaleString()} / ${freq.charAt(0) + freq.slice(1).toLowerCase()}`
    : '—';

const DemoPersonasPage = (): JSX.Element => {
  const theme = useTheme();

  const [rows, setRows] = useState<DemoPersonaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [actionError, setActionError] = useState('');

  const [filterType, setFilterType] = useState<AccountType | ''>('');
  const [editTarget, setEditTarget] = useState<DemoPersonaDTO | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DemoPersonaDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getDemoPersonas(false, filterType || undefined)
      .then((res) => setRows(res.data))
      .catch(() => setError('Could not load personas.'))
      .finally(() => setLoading(false));
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await seedDefaultPersonas();
      setActionMsg('Default personas seeded successfully.');
      load();
    } catch { setActionError('Could not seed default personas.'); }
    finally { setSeeding(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try { await deleteDemoPersona(deleteTarget.id); setDeleteTarget(null); load(); }
    catch { setActionError('Could not delete persona.'); }
    finally { setDeleting(false); }
  };

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight={700}>User Personas</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Reusable persona templates for demo account generation
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh"><IconButton onClick={load} size="small"><RefreshIcon /></IconButton></Tooltip>
              <Button
                variant="outlined"
                onClick={handleSeed}
                disabled={seeding}
              >
                {seeding ? 'Seeding…' : 'Seed Defaults'}
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                New Persona
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
          {actionMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMsg('')}>{actionMsg}</Alert>}

          <Box sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Account type</InputLabel>
              <Select
                value={filterType}
                label="Account type"
                onChange={(e) => setFilterType(e.target.value as AccountType | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PERSONAL">Personal</MenuItem>
                <MenuItem value="FAMILY">Family</MenuItem>
                <MenuItem value="BUSINESS">Business</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Income</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Default Volume</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No personas found. Click "Seed Defaults" to create the standard six.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                            {p.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              >
                                {p.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.accountType.charAt(0) + p.accountType.slice(1).toLowerCase()}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.68rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{p.location ?? '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{fmtCurrency(p.income, p.incomeFrequency)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {p.defaultVolume.charAt(0) + p.defaultVolume.slice(1).toLowerCase()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                fontSize: '0.68rem',
                                height: 22,
                                bgcolor: p.active
                                  ? alpha(theme.palette.success.main, 0.12)
                                  : alpha(theme.palette.error.main, 0.1),
                                color: p.active ? theme.palette.success.dark : theme.palette.error.dark,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Button size="small" variant="outlined" onClick={() => setEditTarget(p)}>
                                Edit
                              </Button>
                              <Button size="small" color="error" onClick={() => setDeleteTarget(p)}>
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Create / edit dialog */}
      {(createOpen || editTarget) && (
        <PersonaFormDialog
          open
          persona={editTarget}
          onClose={() => { setCreateOpen(false); setEditTarget(null); }}
          onSaved={() => { setCreateOpen(false); setEditTarget(null); load(); }}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete persona?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong>? Any demo accounts using this persona will
            keep their existing configuration but lose the persona link.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminDashboard>
  );
};

// ── Inline create/edit form ───────────────────────────────────────────────────

interface PersonaFormProps {
  open: boolean;
  persona: DemoPersonaDTO | null;
  onClose: () => void;
  onSaved: () => void;
}

const PersonaFormDialog = ({ open, persona, onClose, onSaved }: PersonaFormProps): JSX.Element => {
  const [form, setForm] = useState<CreateDemoPersonaRequest>({
    name: persona?.name ?? '',
    accountType: persona?.accountType ?? 'PERSONAL',
    description: persona?.description ?? '',
    location: persona?.location ?? '',
    currency: persona?.currency ?? 'GBP',
    income: persona?.income ?? undefined,
    incomeFrequency: persona?.incomeFrequency ?? 'ANNUAL',
    employmentType: persona?.employmentType ?? '',
    ageMin: persona?.ageMin ?? undefined,
    ageMax: persona?.ageMax ?? undefined,
    householdSize: persona?.householdSize ?? undefined,
    businessType: persona?.businessType ?? '',
    sector: persona?.sector ?? '',
    defaultVolume: persona?.defaultVolume ?? 'MEDIUM',
    defaultHistoricalMonths: persona?.defaultHistoricalMonths ?? 6,
    defaultFutureMonths: persona?.defaultFutureMonths ?? 3,
    active: persona?.active ?? true,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof CreateDemoPersonaRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (persona) {
        await updateDemoPersona(persona.id, form);
      } else {
        await createDemoPersona(form);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Could not save persona.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{persona ? 'Edit Persona' : 'New Persona'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Name" value={form.name} onChange={set('name')} required fullWidth />
          <FormControl fullWidth>
            <InputLabel>Account type</InputLabel>
            <Select
              value={form.accountType}
              label="Account type"
              onChange={(e) => setForm((f) => ({ ...f, accountType: e.target.value as AccountType }))}
            >
              <MenuItem value="PERSONAL">Personal</MenuItem>
              <MenuItem value="FAMILY">Family</MenuItem>
              <MenuItem value="BUSINESS">Business</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Description" value={form.description ?? ''} onChange={set('description')} multiline rows={2} fullWidth />
          <TextField label="Location" value={form.location ?? ''} onChange={set('location')} fullWidth />
          <TextField
            label="Annual Income (GBP)"
            type="number"
            value={form.income ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, income: Number(e.target.value) || undefined }))}
            fullWidth
          />
          <TextField label="Employment type" value={form.employmentType ?? ''} onChange={set('employmentType')} fullWidth />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Age min"
              type="number"
              value={form.ageMin ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, ageMin: Number(e.target.value) || undefined }))}
              fullWidth
            />
            <TextField
              label="Age max"
              type="number"
              value={form.ageMax ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, ageMax: Number(e.target.value) || undefined }))}
              fullWidth
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Default volume</InputLabel>
            <Select
              value={form.defaultVolume}
              label="Default volume"
              onChange={(e) => setForm((f) => ({ ...f, defaultVolume: e.target.value as 'LOW' | 'MEDIUM' | 'HIGH' }))}
            >
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoPersonasPage;
